import { useState } from "react";
import { IconButton } from "@/components/common/icon-button";
import { useCradSize } from "@/hooks/use-card-size";
import { useI18nNavigate } from "@/hooks/use-i18n-navigate";
import { Tooltip } from "@/components/ui/modal";
import { KnowledgeCard } from "./knowledge-card";
import { KnowledgeCreateCard } from "./knowledge-create-card";
import { cn } from "@/lib/utils";
import { CloseIcon, KnowledgeEmptyImage } from "./assets";
import { useTranslation } from "react-i18next";
import { AssetsToolBar } from "@/components/common/assets-tool-bar";
import { assetsKnowledgeHooks } from "@/manager";
import { KnowledgeSkeletonList } from "./knowledge-card-skeleton";
import { MultipleSelectIcon, PlusCircleIcon } from "../assets-material/assets";
import { LoadingOverlay } from "../common/loading-overlay";
import { TextButton } from "../common/text-button";
import { PlusIcon } from "lucide-react";
import { getNextName } from "@/utils/get-next-name";

export const AssetsKnowledge = () => {
  const { t } = useTranslation();
  const navigate = useI18nNavigate();

  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [isCreate, setIsCreate] = useState(false);

  const { knowledgeFolders, loading } = assetsKnowledgeHooks.useAssetsKnowledgeData();
  const { createKnowledge, loading: createLoading } = assetsKnowledgeHooks.useKnowledgeAdd();
  const { renameKnowledge } = assetsKnowledgeHooks.useKnowledgeRename();
  const { deleteKnowledge, loading: deleteLoading } = assetsKnowledgeHooks.useKnowledgeDelete();
  const { loading: downloadLoading, downloadKnowledgeFolder } = assetsKnowledgeHooks.useDownloadKnowledgeFolder();

  const { ref, columns } = useCradSize({
    min: 170,
    max: 220,
    gap: 5,
  });

  // 处理选中单个知识库
  const handleSelected = ({ id, checked }: { id: number; checked: boolean }) => {
    setSelectedIds(prev => {
      const nextSet = new Set(prev);
      if (checked) {
        nextSet.add(id);
      } else {
        nextSet.delete(id);
      }
      return Array.from(nextSet);
    });
  }

  // 处理全选/反选所有知识库
  const handleSelectedAll = (checked: boolean) => {
    setSelectedIds(prev => {
      if (checked) {
        return knowledgeFolders.map(item => item.id);
      }
      return [];
    });
  }

  // 处理取消选择/退出多选模式
  const handleCancelSelect = () => {
    setSelectedIds([]);
  }

  // 批量删除
  const handleBatchDelete = async () => {
    setIsCreate(false)
    deleteKnowledge(selectedIds);
    handleCancelSelect();
  }

  // 单个删除
  const handleSingleDelete = async (id: number) => {
    setIsCreate(false)
    await deleteKnowledge(id);
  }

  // 处理下载选中文件夹
  const handleSingleDownload = async (id?: number) => {
    await downloadKnowledgeFolder([id]);
  }

  const handlePatchDownload = async () => {
    handleCancelSelect();
    await downloadKnowledgeFolder(selectedIds);
  }

  // 重命名
  const handleRename = (id: number, newName: string) => {
    renameKnowledge({ id, name: newName });
    setIsCreate(false)
  }

  // 处理创建知识库
  const handleCreate = async () => {
    try {
      const nextName = getNextName(knowledgeFolders, t('assets.knowledge.untitled'));
      await createKnowledge({ name: nextName })
      setTimeout(() => {
        setIsCreate(true);
      }, 100);
    } catch (error) {
      console.log(error)
    }
  }

  // 跳转详情页
  const handleDetail = (id: number) => {
    navigate(`/assets/knowledge/${id}/`);
  }

  // 处理容器点击（空白处点击，如果有出现点击其他地方关闭了工具栏，考虑是否是这里的问题）
  const handleContainerClick = (e: React.MouseEvent) => {
    // 如果当前没有选中的项，或者点击的是工具栏等 UI，不触发
    if (selectedIds.length > 0) {
      handleCancelSelect();
    }
  };

  return (
    <div className="size-full" onClick={handleContainerClick}>
      <div className="relative h-full">
        <div className="flex flex-col relative p-8 pt-3 h-full overflow-hidden">
          <header className="flex justify-between items-center mb-[18px] z-[50]">
            <div className="text-[20px] font-bold">{t('assets.knowledge.title')}</div>
            <Tooltip
              side="top"
              align="center"
              trigger={
                <IconButton
                  className="size-8"
                  onClick={handleCreate}
                  icon={<PlusCircleIcon className="size-6" />}
                />
              }
              content={
                <div className='relative bg-[rgba(16,24,40,1)] px-3 py-2 rounded-[8px]'>
                  <span className='text-[rgba(255,255,255,1)] text-sm font-medium'>
                    {t('assets.material.create_folder')}
                  </span>
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-[6px] border-r-[6px] border-b-[6px] border-l-transparent border-r-transparent border-b-black"></div>
                </div>
              }
            />
          </header>
          <div className="h-full overflow-y-auto pt-2 ">
            <div
              ref={ref}
              className='grid gap-5 gap-y-24 mb-20'
              style={{ gridTemplateColumns: `repeat(${columns}, 1fr)` }}
            >
              {/* <KnowledgeCreateCard onCreate={handleCreate} loading={createLoading} /> */}
              {loading && <KnowledgeSkeletonList count={15} />}
              {!loading && knowledgeFolders.length > 0 && knowledgeFolders.map((item, index) => (
                <KnowledgeCard
                  key={item.id}
                  id={item.id}
                  name={item.folderName}
                  time={item.createTime}
                  isSelected={selectedIds.includes(item.id)}
                  openSelected={selectedIds.length > 0}
                  isEditName={index === 0 && isCreate && !createLoading}
                  onSelected={handleSelected}
                  onDelete={handleSingleDelete}
                  onDownload={handleSingleDownload}
                  onRename={handleRename}
                  onDetail={handleDetail}
                />
              ))}
            </div>
            {!loading && !knowledgeFolders.length && (
              <div className="absolute inset-0  flex items-center justify-center">
                <div className="flex flex-col items-center">
                  <KnowledgeEmptyImage className="w-[211px] mb-3" />
                  <span className="text-[rgba(102,112,133,1)] text-sm mb-4">
                    {t('assets.knowledge.empty_tips')}
                  </span>
                  <TextButton
                    icon={<PlusIcon className="size-5" />}
                    onClick={handleCreate}
                    label={t('assets.knowledge.create_folder')}
                    className="text-sm font-medium"
                    loading={createLoading}
                    disabled={createLoading}
                  />
                </div>
              </div>
            )}
          </div>
          <div
            className={cn(
              "transition-all duration-300 ease-in-out",
              selectedIds.length > 0 ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6 pointer-events-none"
            )}
            onClick={(e) => e.stopPropagation()}
          >
            <AssetsToolBar
              onClose={handleCancelSelect}
              isSelectedAll={selectedIds.length === knowledgeFolders.length}
              selectedAmount={selectedIds.length}
              onSelectedAll={handleSelectedAll}
              onDelete={handleBatchDelete}
              onDownload={handlePatchDownload}
            />
          </div>
        </div>
        <LoadingOverlay loading={deleteLoading || createLoading} />
      </div>
      <LoadingOverlay loading={downloadLoading} text={t('assets.material.preparing_download')} />
    </div>
  )
}
